import { type FC, useState } from "react";
import { Navbar } from "../../components/layout/NavBar";
import { ModalCambiarContrasena } from "../../components/modals/ModalCambiarContrasena/ModalCambiarContrasena";
import { ModalNoAutorizado } from "../../components/modals/ModalNoAutorizado/ModalNoAutorizado";
import { useAuthState, useClientesState, useRutasState } from "../../context/AppContext";
import { formatDate, formatearNumeros } from "../../utils/formatters";
import { ConfirmarCierreCajaModal } from "./components/ConfirmarCierreCajaModal";
import { TablaCierresCajaModal } from "./components/TablaCierresCajaModal";
import { TablaMovimientosModal } from "./components/TablaMovimientosModal";
import {
  useCambiarContrasenaHandler,
  useCierreCajaHandler,
  useEstadisticasPageMetrics,
} from "./estadisticasPageHooks";
import type { ClienteEstadisticas, EstadisticasPageProps } from "./types";

export const EstadisticasPage: FC<EstadisticasPageProps> = () => {
  const { userData } = useAuthState();
  const { infoClientes, prestamoDelDia, totalAbonos } = useClientesState();
  const { rutasData, totalMonto1, totalMonto2, usuarioRuta } = useRutasState();

  const [mostrarMovimientos, setMostrarMovimientos] = useState(false);
  const [verCambiarContrasenaModal, setverCambiarContrasenaModal] = useState(false);
  const [verModalNoautorizado, setVerModalNoautorizado] = useState(false);

  const {
    gastosHoy,
    ultimoSaldo,
    // gananciasRutas,
    saldoActual,
    saldoMasNuevo,
    // ganancias,
    saldoRutas,
  } = useEstadisticasPageMetrics({
    usuarioRuta,
    rutasData,
  });

  const handleCambiarContrasenha = useCambiarContrasenaHandler({
    usuarioRuta,
    userData,
    setverCambiarContrasenaModal,
    setVerModalNoautorizado,
  });
  const {
    canUseCierreCaja,
    handleCloseConfirmarCierreCaja,
    handleCloseTablaCierresCaja,
    handleConfirmarCierreCaja,
    handleOpenConfirmarCierreCaja,
    handleOpenTablaCierresCaja,
    isCloseCajaDisabled,
    isClosingCaja,
    isConfirmarCierreCajaOpen,
    isTablaCierresCajaOpen,
  } = useCierreCajaHandler({
    infoClientes: infoClientes as ClienteEstadisticas[] | null,
    prestamoDelDia,
    totalAbonos,
    usuarioRuta,
    userData,
  });

  return (
    <>
      {isConfirmarCierreCajaOpen ? (
        <ConfirmarCierreCajaModal
          isSubmiting={isClosingCaja}
          onCancel={handleCloseConfirmarCierreCaja}
          onConfirm={() => void handleConfirmarCierreCaja()}
          usuarioRuta={usuarioRuta}
        />
      ) : null}

      {isTablaCierresCajaOpen ? (
        <TablaCierresCajaModal
          formatDate={formatDate}
          onClose={handleCloseTablaCierresCaja}
          usuarioRuta={usuarioRuta}
        />
      ) : null}

      {verCambiarContrasenaModal ? (
        <ModalCambiarContrasena setverCambiarContrasenaModal={setverCambiarContrasenaModal} />
      ) : null}

      {verModalNoautorizado ? (
        <ModalNoAutorizado setVerModalNoautorizado={setVerModalNoautorizado} />
      ) : null}

      {mostrarMovimientos ? (
        <TablaMovimientosModal
          ingresos={totalMonto1}
          salidas={totalMonto2}
          formatDate={formatDate}
          usuarioRuta={usuarioRuta}
          setMostrarMovimientos={setMostrarMovimientos}
        />
      ) : null}

      <Navbar />
      <div className="w-full min-h-screen bg-gray-200 pt-16 px-4 md:px-8">
        <div>
          <p>
            Saldo Actual:{" "}
            <span className="font-bold">
              {userData
                ? `R$${formatearNumeros(saldoRutas)}`
                : `R$${formatearNumeros(saldoActual)}`}
            </span>
          </p>

          <p>
            {userData ? "Total saldo invertido: " : "Ultimo saldo ingresado: "}
            <span className="font-bold">
              R$
              {saldoMasNuevo > 0 ? formatearNumeros(saldoMasNuevo) : formatearNumeros(ultimoSaldo)}
            </span>
          </p>

          {userData ? (
            <p>
              Total rutas:
              <span className="font-bold ml-1">{rutasData?.length}</span>
            </p>
          ) : (
            <p>
              Total Clientes:
              <span className="font-bold ml-1">{infoClientes?.length}</span>
            </p>
          )}

          {userData ? null : (
            <>
              <p>
                Cobro del dia: <span className="font-bold">R${formatearNumeros(totalAbonos)}</span>
              </p>
              <p>
                Prestamos del dia:{" "}
                <span className="font-bold">R${formatearNumeros(prestamoDelDia)}</span>
              </p>
              <p>
                Gastos del dia: <span className="font-bold">R${formatearNumeros(gastosHoy)}</span>
              </p>
              <div className="mt-2 flex flex-col items-start gap-2">
                <button
                  type="button"
                  className="bg-[#8131bd] w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[80px]"
                  onClick={() => setMostrarMovimientos(true)}
                >
                  Movimientos
                </button>

                {canUseCierreCaja ? (
                  <>
                    <button
                      type="button"
                      className="bg-[#8131bd] w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[80px]"
                      onClick={handleOpenTablaCierresCaja}
                    >
                      Ver cierres
                    </button>
                    <button
                      type="button"
                      className={`w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[100px] ${
                        isCloseCajaDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-[#8131bd]"
                      }`}
                      disabled={isCloseCajaDisabled}
                      onClick={handleOpenConfirmarCierreCaja}
                    >
                      Cerrar caja
                    </button>
                  </>
                ) : null}

                <button
                  type="button"
                  className="bg-[#8131bd] w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[80px]"
                  onClick={handleCambiarContrasenha}
                >
                  Cambiar Contrasena
                </button>
              </div>
            </>
          )}

          {userData ? (
            <button
              type="button"
              className="bg-[#8131bd] mt-2 w-fit text-white px-2 py-1 rounded-md flex justify-center items-center min-w-[80px]"
              onClick={handleCambiarContrasenha}
            >
              Cambiar Contrasena
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
};
